import React, { useEffect, useState } from "react"

import { validatorTypes } from "../../hooks/forms/forms"
import useCaptcha from "../../hooks/forms/useCaptcha"
import useInput from "../../hooks/forms/useInput"
import { sendNewsletterForm } from "../../service/rest"
import Form from "../Form/Form"
import Input from "../Form/Input"
import Submit from "../Form/Submit"
import s from "./NewsletterModal.module.scss"

const CAPTCHA_ACTION = "form_burgerking_subscribe"

export default function NewsletterModal({
  i18n,
  langCode,
  onClose,
  onPrivacyPolicyClick,
}) {
  const [loading, setLoading] = useState(false)
  const [errorsVisible, setErrorsVisible] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const [consentError, setConsentError] = useState(false)

  const captcha = useCaptcha()
  const fields = {
    name: useInput({ initialValue: "", errorsVisible, required: true }),
    email: useInput({
      initialValue: "",
      errorsVisible,
      required: true,
      validatorType: validatorTypes.email,
    }),
    dateOfBirth: useInput({
      initialValue: "",
      errorsVisible,
      required: true,
      validatorType: validatorTypes.dateOfBirth,
    }),
  }

  useEffect(() => {
    document.documentElement.classList.add("open-popup")
    return () => document.documentElement.classList.remove("open-popup")
  }, [])

  return (
    <div className={s.overlay} onClick={handleOverlayClick}>
      <div className={s.modal} role="dialog" aria-modal="true">
        <button
          aria-label="Close"
          className={s.closeButton}
          onClick={onClose}
          type="button"
        >
          ×
        </button>

        {succeeded ? (
          <div className={s.success}>
            <h2 className={s.successTitle}>{i18n.successTitle}</h2>
            <p className={s.successText}>{i18n.successText}</p>
            <button className={s.doneButton} onClick={onClose} type="button">
              OK
            </button>
          </div>
        ) : (
          <>
            <h2 className={s.title}>{i18n.title}</h2>
            <p className={s.subtitle}>{renderSubtitle()}</p>
            <Form className={s.form} onSubmit={handleSubmit}>
              <Input
                label={i18n.nameLabel}
                name="name"
                {...fields.name.props}
              />
              <Input
                label={i18n.emailLabel}
                name="email"
                type="email"
                {...fields.email.props}
              />
              <Input
                label={i18n.dateOfBirthLabel}
                name="dateOfBirth"
                type="date"
                {...fields.dateOfBirth.props}
              />
              <div className={s.consentField}>
                <label className={s.consentLabel}>
                  <input
                    checked={consentChecked}
                    className={s.consentCheckbox}
                    onChange={(e) => {
                      setConsentChecked(e.target.checked)
                      setConsentError(false)
                    }}
                    type="checkbox"
                  />
                  <span>
                    {i18n.consentLabel}{" "}
                    <a
                      className={s.privacyLink}
                      onClick={(e) => {
                        e.preventDefault()
                        onPrivacyPolicyClick?.()
                      }}
                      href="/privacy-policy"
                    >
                      {i18n.consentLinkText}
                    </a>
                    .
                  </span>
                </label>
                {consentError && (
                  <span className={s.consentError}>
                    {i18n.consentErrorLabel}
                  </span>
                )}
              </div>
              <Submit
                disabled={loading}
                value={loading ? i18n.sendingLabel : i18n.submitLabel}
              />
              {submitError && (
                <p className={s.submitError}>{i18n.submitErrorText}</p>
              )}
            </Form>
          </>
        )}
      </div>
    </div>
  )

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!consentChecked) setConsentError(true)

    if (isFormValid() && consentChecked) {
      submitForm()
    } else {
      setErrorsVisible(true)
    }
  }

  function isFormValid() {
    return Object.values(fields).every((f) => f.valid)
  }

  function renderSubtitle() {
    const { subtitle, subtitleHighlight } = i18n
    if (!subtitleHighlight) return subtitle
    const idx = subtitle.indexOf(subtitleHighlight)
    if (idx === -1) return subtitle
    return (
      <>
        {subtitle.slice(0, idx)}
        <span className={s.highlight}>{subtitleHighlight}</span>
        {subtitle.slice(idx + subtitleHighlight.length)}
      </>
    )
  }

  async function submitForm() {
    setLoading(true)
    setSubmitError(false)
    try {
      const token = await captcha.getToken(CAPTCHA_ACTION)
      await sendNewsletterForm({
        name: fields.name.value,
        email: fields.email.value,
        dateOfBirth: fields.dateOfBirth.value,
        market: "Estonia",
        language: langCode.toUpperCase(),
        captchaToken: token,
        captchaAction: CAPTCHA_ACTION,
        captchaUserId: fields.email.value,
      })
      setSucceeded(true)
    } catch {
      setSubmitError(true)
    } finally {
      setLoading(false)
    }
  }
}
